import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IConnection, IFormProps, INestedSchema, IProjectProps } from "data/types";
import { LabelMap } from "data/Sample";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";

export interface ProjectCacheData {
  forms: INestedSchema;
  flatForms: IFormProps[];
  connections: IConnection[];
  samples: LabelMap;
}

interface ProjectCacheContextValue {
  /** Returns the cached data for projectId, or undefined if nothing (or a different project) is cached. */
  get: (projectId: string) => ProjectCacheData | undefined;
  /** Caches data for projectId, replacing whatever was previously cached. */
  set: (projectId: string, data: ProjectCacheData) => void;
  /** Drops whatever is currently cached. Call this as soon as a different project starts loading. */
  clear: () => void;
  /** Returns the cached full project list (from list-projects), or undefined if not yet loaded. */
  getProjects: () => IProjectProps[] | undefined;

  getProjectById: (uuid: string) => IProjectProps | undefined;
}

const ProjectCacheContext = createContext<ProjectCacheContextValue | undefined>(undefined);

/**
 * Wraps the app to hold a single-slot cache of the most recently loaded project's
 * info, connections, and Sample Browser data, so navigating back to that same
 * project doesn't have to refetch everything.
 *
 * This lives in a Context (rather than component state) because Project.tsx and
 * its sibling pages are force-remounted on every navigation (see ProjectLayout's
 * `key={Date.now()}` on each nested route), so nothing stored on the component
 * itself would ever survive a navigation.
 */
export const ProjectCacheProvider: FunctionComponent<PropsWithChildren<{}>> = ({ children }) => {

  const cache = useRef<{ projectId: string; data: ProjectCacheData } | undefined>(undefined);
  const [projects, setProjectsState] = useState<IProjectProps[] | undefined>(undefined);
  const cartaObjectStorage = useCartaObjectStorage();


  const get = useCallback((projectId: string) => {
    return cache.current?.projectId === projectId ? cache.current.data : undefined;
  }, []);

  const set = useCallback((projectId: string, data: ProjectCacheData) => {
    cache.current = { projectId, data };
  }, []);

  const clear = useCallback(() => {
    cache.current = undefined;
  }, []);

  const getProjects = useCallback(() => projects, [projects]);

  const getProjectById = useCallback((uuid: string)=>{
    return projects?.find(p=>p.projectId===uuid)
  }, [projects])

  const value = useMemo(
    () => ({ get, set, clear, getProjects, getProjectById }),
    [get, set, clear, getProjects, getProjectById]
  );

  
  // Automatically load the project list once on startup, as soon as the API
  // client is available, rather than waiting for a page to ask for it.
  useEffect(() => {
    clear();
    if(!cartaObjectStorage) return;

    cartaObjectStorage.listProjects().then(projects=>setProjectsState(projects))

  }, [cartaObjectStorage, clear]);

  return <ProjectCacheContext.Provider value={value}>{children}</ProjectCacheContext.Provider>;
};

export function useProject(): ProjectCacheContextValue {
  const context = useContext(ProjectCacheContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectCacheProvider");
  }
  return context;
}
